import { useMemo } from 'react';
import {
  FileText,
  Pencil,
  Trash2,
  Move,
  MessageSquare,
  Paperclip,
  UserPlus,
  UserMinus,
  FolderPlus,
  Settings,
  Flag,
  Loader2,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useProjectActivity, ActivityEntry } from '@/hooks/useActivity';

interface ActivityFeedProps {
  projectId: string;
  className?: string;
}

const ACTIVITY_ICONS: Record<string, typeof FileText> = {
  task_created: FolderPlus,
  task_updated: Pencil,
  task_deleted: Trash2,
  task_moved: Move,
  comment_added: MessageSquare,
  attachment_added: Paperclip,
  member_added: UserPlus,
  member_removed: UserMinus,
  project_created: FolderPlus,
  project_updated: Settings,
  baseline_created: Flag,
};

const ACTIVITY_COLORS: Record<string, string> = {
  task_created: 'text-green-600 bg-green-50',
  task_updated: 'text-blue-600 bg-blue-50',
  task_deleted: 'text-red-600 bg-red-50',
  task_moved: 'text-purple-600 bg-purple-50',
  comment_added: 'text-amber-600 bg-amber-50',
  attachment_added: 'text-cyan-600 bg-cyan-50',
  member_added: 'text-green-600 bg-green-50',
  member_removed: 'text-red-600 bg-red-50',
  project_created: 'text-indigo-600 bg-indigo-50',
  project_updated: 'text-gray-600 bg-gray-50',
  baseline_created: 'text-orange-600 bg-orange-50',
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function ActivityItem({ activity }: { activity: ActivityEntry }) {
  const Icon = ACTIVITY_ICONS[activity.activityType] || FileText;
  const colorClass = ACTIVITY_COLORS[activity.activityType] || 'text-gray-600 bg-gray-50';
  const userName = activity.user.displayName || activity.user.email;

  return (
    <div className="flex gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">
          <span className="font-medium">{userName}</span>
          {' '}
          <span className="text-gray-600">{activity.description}</span>
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {formatRelativeTime(activity.createdAt)}
        </p>
      </div>
    </div>
  );
}

function groupByDate(activities: ActivityEntry[]): Map<string, ActivityEntry[]> {
  const groups = new Map<string, ActivityEntry[]>();
  for (const activity of activities) {
    const date = new Date(activity.createdAt).toLocaleDateString();
    const existing = groups.get(date) || [];
    existing.push(activity);
    groups.set(date, existing);
  }
  return groups;
}

export function ActivityFeed({ projectId, className }: ActivityFeedProps) {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProjectActivity(projectId);

  const allActivities = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.activities);
  }, [data]);

  const grouped = useMemo(() => groupByDate(allActivities), [allActivities]);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className || ''}`}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (allActivities.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-8 text-center ${className || ''}`}>
        <FileText className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">No activity yet</p>
        <p className="text-xs text-gray-400 mt-1">Changes to tasks will appear here</p>
      </div>
    );
  }

  return (
    <ScrollArea className={`h-full ${className || ''}`}>
      <div className="divide-y divide-gray-100">
        {Array.from(grouped.entries()).map(([date, activities]) => (
          <div key={date}>
            <div className="sticky top-0 bg-gray-50 px-4 py-1.5 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500">{date}</span>
            </div>
            {activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        ))}
      </div>

      {hasNextPage && (
        <div className="p-4 text-center">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </ScrollArea>
  );
}

export default ActivityFeed;
